// Centralized phone number derivation utility
// Ensures consistent phone number logic across all components

interface ActionData {
  caller_id?: string;
  caller_name?: string;
  phone_number?: string;
  callback_number?: string;
  dialed_number?: string;
  [key: string]: any;
}

interface CallAction {
  action_data?: ActionData;
  mode?: string;
  campaigns?: {
    campaign_type?: string;
  };
}

interface PhoneNumbers {
  primary: string | null;      // The primary number for contact (always equals callback)
  callback: string | null;     // The number to call back (same as primary)
  callerId: string | null;     // The actual caller's number (inbound only)
  dialed: string | null;       // The number we dialed (outbound only)
  isDifferent: boolean;        // True if callback differs from caller ID/dialed
}

/**
 * Derives standardized phone numbers from call actions
 * 
 * Rules:
 * - Primary ALWAYS equals Callback (user requirement)
 * - For inbound: Primary/Callback = callback_number || caller_id, fallback to Caller ID
 * - For outbound: Primary/Callback = callback_number || phone_number, show Dialed separately
 */
export function derivePhones(actions: CallAction[], fallbackPhone?: string): PhoneNumbers {
  if (!actions || actions.length === 0) {
    return {
      primary: fallbackPhone || null,
      callback: fallbackPhone || null,
      callerId: null,
      dialed: null,
      isDifferent: false
    };
  }

  // Determine call direction
  const isInbound = actions.some(a => 
    a.mode === 'inbound' || 
    a.campaigns?.campaign_type?.includes('inbound')
  );

  // Aggregate all action data
  const allActionData = actions.map(a => a.action_data).filter(Boolean);
  
  let callerId: string | null = null;
  let dialedNumber: string | null = null;
  let callbackNumber: string | null = null;

  // Extract phone numbers from all actions
  for (const data of allActionData) {
    // Caller ID (who called us)
    if (data.caller_id && !callerId) {
      callerId = data.caller_id;
    }
    
    // Dialed number (who we called)
    if (data.phone_number && !dialedNumber) {
      dialedNumber = data.phone_number;
    }
    if (data.dialed_number && !dialedNumber) {
      dialedNumber = data.dialed_number;
    }
    
    // Explicit callback number (priority override)
    if (data.callback_number && !callbackNumber) {
      callbackNumber = data.callback_number;
    }
  }

  if (isInbound) {
    // For inbound calls:
    // - Primary/Callback = explicit callback_number (from transcript) OR fallback phone OR caller_id
    // - Caller ID = the actual caller (may be same as primary)
    // - No dialed number (we didn't dial out)
    
    // If no explicit caller_id found, check phone_number as fallback
    if (!callerId && allActionData.some(d => d.phone_number)) {
      callerId = allActionData.find(d => d.phone_number)!.phone_number;
    }
    
    // Priority: transcript-extracted callback_number > fallback (transcript-parsed) > caller_id
    const primary = callbackNumber || fallbackPhone || callerId || null;
    
    return {
      primary,
      callback: primary, // Always same as primary
      callerId,
      dialed: null, // No outbound dialing for inbound calls
      isDifferent: callbackNumber ? callbackNumber !== callerId : false
    };
  } else {
    // For outbound calls:
    // - Primary/Callback = explicit callback_number OR dialed number
    // - Dialed = the number we actually dialed
    // - No caller ID (they didn't call us)
    
    const primary = callbackNumber || dialedNumber || fallbackPhone || null;
    
    return {
      primary,
      callback: primary, // Always same as primary  
      callerId: null, // No incoming caller for outbound calls
      dialed: dialedNumber,
      isDifferent: callbackNumber ? callbackNumber !== dialedNumber : false
    };
  }
}

/**
 * Get display text for phone numbers based on call direction
 */
export function getPhoneLabels(isInbound: boolean) {
  return {
    primary: 'Primary (callback)',
    secondary: isInbound ? 'Caller ID' : 'Dialed',
    differentHint: isInbound ? 'Different from caller ID' : 'Different from dialed'
  };
}